'use client';

import {useEffect, useRef} from 'react';

// Flowing particle-wave rendered along the bottom of the home hero.
// Pure canvas, time-based so it stays smooth regardless of frame rate.
const LINES = 22;
const PARTICLES = 100;

const HeroWave = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    let running = true;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const {width, height} = canvas.getBoundingClientRect();
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;


    const waveY = (x, w, h, i, t) => {
      const base = h * 0.55 + (i - LINES / 2) * (h * 0.03);
      return base
        + Math.sin(x * 0.0045 + t * 0.00045 + i * 0.55) * h * 0.16
        + Math.sin(x * 0.0011 - t * 0.00028 + i * 1.7) * h * 0.1;
    };

    const draw = (t) => {
      const w = canvas.getBoundingClientRect().width;
      const h = canvas.getBoundingClientRect().height;
      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < LINES; i++) {
        ctx.beginPath();
        for (let x = 0; x <= w; x += 14) {
          const y = waveY(x, w, h, i, t);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        const green = i % 5 === 0;
        ctx.strokeStyle = green ? 'rgba(69, 207, 109, 0.16)' : `rgba(52, 149, 219, ${0.08 + (i % 4) * 0.05})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      for (let p = 0; p < PARTICLES; p++) {
        const i = p % LINES;
        const x = ((p * 137.5 + t * (0.012 + (p % 7) * 0.004)) % (w + 60)) - 30;
        const y = waveY(x, w, h, i, t);
        const r = 1 + (p % 3);
        const glow = p % 9 === 0;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = glow ? 'rgba(141, 242, 254, 0.9)' : 'rgba(52, 149, 219, 0.55)';
        if (glow) {
          ctx.shadowColor = 'rgba(141, 242, 254, 0.9)';
          ctx.shadowBlur = 8;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      if (running && !reduceMotion) raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className='hero-wave' aria-hidden='true' />;
};

export default HeroWave;
